import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { AssetManagementCategoryAccountsGridComponent } from '../../components/asset-management-account-categories-grid/asset-management-category-accounts-grid.component';
import { AsyncPipe } from '@angular/common';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { BehaviorSubject, filter, noop, switchMap } from 'rxjs';
import {
  CategoryAccountManagementDialogComponent,
  CategoryAccountDialogData,
} from '../../components/category-account-management-dialog/category-account-management-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take, tap } from 'rxjs/operators';
import { CategoryAccount } from '../../models/category-account';
import { Store } from '@ngxs/store';
import { AssetManagementActions } from '../../state/asset-management.actions';
import { AssetManagementState } from '../../state/asset-management.state';
import { TsRxjsOperators } from '@shared/Utils/custom-rxjs-operators.utils';
import { AssetManagementMessages } from '../../utils/asset-management-messages.utils';
import { AssetManagementLabels } from '../../enums/asset-management-reports.enum';
import { TsSnackbarService } from 'tsui';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';
import { BatchUpdateCategoryAccountEntry } from '../../models';

@Component({
  selector: 'asset-management-global-settings-container',
  standalone: true,
  imports: [AssetManagementCategoryAccountsGridComponent, AsyncPipe],
  template: `
    <asset-management-category-accounts-grid
      [loading]="categoryAccountsLoading | async"
      [categoryAccounts]="categoryAccounts$ | async"
      (manageCategoryAccount)="openCategoryAccountManagementDialog($event)"
      (deleteCategoryAccount)="deleteCategoryAccount($event)"
      (updateCategoryAccounts)="updateCategoryAccounts($event)"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetManagementGlobalSettingsContainerComponent implements OnInit {
  private readonly dialog = inject(Dialog);
  private readonly store = inject(Store);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbar = inject(TsSnackbarService);

  private readonly loadCategoryAccountsTrigger = new BehaviorSubject<void>(null);
  readonly categoryAccountsLoading = new BehaviorSubject<boolean>(true);
  readonly categoryAccounts$ = this.store.select(AssetManagementState.categoryAccounts);

  ngOnInit() {
    this.loadCategoryAccountsTrigger
      .pipe(
        tap(() => this.categoryAccountsLoading.next(true)),
        switchMap(() => this.store.dispatch(new AssetManagementActions.LoadCategoryAccounts())),
        tap(() => this.categoryAccountsLoading.next(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(noop);
  }

  openCategoryAccountManagementDialog(categoryAccount?: CategoryAccount) {
    const dialogRef: DialogRef = this.dialog.open(CategoryAccountManagementDialogComponent, {
      data: {
        ...(!categoryAccount ? {} : this.formatCategoryAccountToCategoryAccountDialogData(categoryAccount)),
      },
      ...TsDialogComponent.getDefaultDialogConfig(),
    });

    dialogRef.closed
      .pipe(
        filter((result) => !!result),
        switchMap((result: CategoryAccountDialogData) => {
          if (result.id) {
            return this.store
              .dispatch(new AssetManagementActions.UpdateCategoryAccount(result))
              .pipe(
                TsRxjsOperators.successAndErrorSnackbar(
                  this.snackbar,
                  AssetManagementMessages.Success.UPDATE(AssetManagementLabels.CategoryAccount),
                  AssetManagementMessages.Error.UPDATE(AssetManagementLabels.CategoryAccount),
                ),
              );
          }
          return this.store
            .dispatch(new AssetManagementActions.CreateCategoryAccount(result))
            .pipe(
              TsRxjsOperators.successAndErrorSnackbar(
                this.snackbar,
                AssetManagementMessages.Success.CREATE(AssetManagementLabels.CategoryAccount),
                AssetManagementMessages.Error.CREATE(AssetManagementLabels.CategoryAccount),
              ),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.loadCategoryAccountsTrigger.next();
      });
  }

  updateCategoryAccounts(categoryAccounts: BatchUpdateCategoryAccountEntry[]) {
    this.store
      .dispatch(new AssetManagementActions.UpdateCategoryAccounts(categoryAccounts))
      .pipe(
        take(1),
        TsRxjsOperators.successAndErrorSnackbar(
          this.snackbar,
          AssetManagementMessages.Success.UPDATE(AssetManagementLabels.CategoryAccounts),
          AssetManagementMessages.Error.UPDATE(AssetManagementLabels.CategoryAccounts),
        ),
      )
      .subscribe(() => {
        this.loadCategoryAccountsTrigger.next();
      });
  }

  deleteCategoryAccount(categoryAccount: CategoryAccount) {
    this.confirmationDialogService
      .openConfirmationDialog({
        dialogTitle: `Delete Account: ${categoryAccount?.name}`,
        ...ConfirmationDialogService.deleteConfirmationCommonLayoutConfig(),
        ...(categoryAccount.children.length > 1
          ? { description: 'This action will delete the account and all its children accounts' }
          : {}),
      })
      .pipe(
        filter((result) => result),
        tap(() => this.categoryAccountsLoading.next(true)),
        switchMap(() => this.store.dispatch(new AssetManagementActions.DeleteCategoryAccount(categoryAccount))),
        tap(() => this.categoryAccountsLoading.next(false)),
        take(1),
      )
      .subscribe(() => {
        this.loadCategoryAccountsTrigger.next();
      });
  }

  private formatCategoryAccountToCategoryAccountDialogData({
    id,
    name,
    description,
    parent_id,
    normal_balance,
    account_type,
    report_type,
    total_aggregates,
  }: CategoryAccount): CategoryAccountDialogData {
    return {
      id,
      name,
      description,
      parent_id,
      normal_balance,
      account_type,
      report_type,
      total_aggregates,
    };
  }
}
